import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TextField, Drawer, Box, Button, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useAppointmentAnalyses from 'hooks/useAppointmentAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { appointmentAnalysesSelectors } from 'features/appointmentAnalyses';

const AppointmentAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { appointment_analysis_id } = useParams();
  const { getAppointmentAnalysis, clearLocalActiveAppointmentAnalysis } = useAppointmentAnalyses();
  const { getAppointmentAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const appointmentAnalysisFetching = useCustomSelector(appointmentAnalysesSelectors.appointmentAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchAppointmentAnalysis = useCallback((id) => {
    setOpen(true);
    getAppointmentAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { appointment_analysis } } = payload;
        handleSetData(appointment_analysis);
      }
    })
  }, [getAppointmentAnalysis]);

  const handleSetData = (appointment_analysis) => {
    setInput(appointment_analysis?.input);
    setResult(appointment_analysis?.result);
  };

  const handleExiting = () => {
    setInput('');
    setResult('');
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl88a`);
    clearLocalActiveAppointmentAnalysis();
    clearLocalAnalysesEvents();

  };

  const handleClose = () => setOpen(false);

  const fetchAppointmentAnalysesEvents = useCallback((offset, appointment_analysis_id) => {
    getAppointmentAnalysesEvents({ offset, appointment_analysis_id })
  }, [ getAppointmentAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchAppointmentAnalysesEvents(0, appointment_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!appointment_analysis_id) fetchAppointmentAnalysis(appointment_analysis_id);
  }, [appointment_analysis_id, fetchAppointmentAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { appointmentAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {appointment_analysis_id}</Typography>
          <TextField
            fullWidth
            margin='normal'
            label='Input'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={input ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <TextField
            fullWidth
            margin='normal'
            label='Result'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={result ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={appointment_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchAppointmentAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
      <Button
        variant='text'
        color='error'
        sx={{ display: 'flex', mx: 'auto', fontSize: '1rem', mb: '15px' }}
        disabled={appointmentAnalysisFetching}
        onClick={() => {}}
      >
        Abort Execution
      </Button>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <AppointmentAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);